import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Label } from "components/input/Label";
import { camposTributariosItem, EmitirNotaFiscalSaida, ItemEmissaoNotaFiscalSaida, OpcoesEmissaoNotaFiscalSaida } from "model/vendas/EmitirNotaFiscalSaida";
import Cfop from "model/fiscal/Cfop";
import Produto from "model/administracao/cadastro/produto/Produto";
import EstoqueLote from "model/suprimentos/EstoqueLote";
import NotaFiscalSaidaService from "service/vendas/NotaFiscalSaidaService";
import NotaFiscalSaidaCalculoService from "service/vendas/NotaFiscalSaidaCalculoService";
import NotaFiscalSaidaItemService from "service/vendas/NotaFiscalSaidaItemService";
import ProdutoService from "service/administracao/cadastro/produto/ProdutoService";
import { isAxiosError } from "axios";

const service = new NotaFiscalSaidaService();
const calculo = new NotaFiscalSaidaCalculoService();
const itens = new NotaFiscalSaidaItemService();
const produtos = new ProdutoService();
const simNao = [{ label: "Sim", value: true }, { label: "Não", value: false }];
type Campo = keyof ItemEmissaoNotaFiscalSaida;
type Tributo = "Icms" | "Ipi" | "Pis" | "Cofins" | "Ibs" | "Cbs";
interface Props {
    nota: EmitirNotaFiscalSaida;
    item: ItemEmissaoNotaFiscalSaida;
    indice: number;
    novo: boolean;
    opcoes: OpcoesEmissaoNotaFiscalSaida;
    cfops: Cfop[];
    onSave: (item: ItemEmissaoNotaFiscalSaida) => void;
    onHide: () => void;
}

export default function NotaFiscalSaidaItemModal(props: Props) {
    const [item, setItem] = useState<ItemEmissaoNotaFiscalSaida>(() => ({ ...props.item }));
    const [consultando, setConsultando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [erroFiscal, setErroFiscal] = useState(false);
    const [lotes, setLotes] = useState<EstoqueLote[]>([]);
    const [consultandoLotes, setConsultandoLotes] = useState(false);
    const informados = useRef<Partial<ItemEmissaoNotaFiscalSaida>>(props.novo ? {} : itens.valoresInformados(props.item));
    const atual = useRef(item);
    atual.current = item;
    const primeiraConsulta = useRef(!props.novo && !!props.item.produto);
    const transacao = itens.transacao(item, props.nota);
    const icms51SemCalculo = item.cstIcms === "51" && item.informarCalculoIcms51 === false;
    const diferimentoIcms = calculo.calcularDiferimentoIcms(item, transacao);
    const contexto = JSON.stringify([props.nota.unidadeOrganizacional?.id, transacao?.id,
        props.nota.destinatario?.id, props.nota.enderecoDestinatario?.id, props.nota.consumidorFinal,
        props.nota.dataEmissao, props.nota.finalidade, !!props.nota.transacaoNotaFiscal, item.produto?.codigo,
        item.informarCalculoIcms51 !== false]);

    useEffect(() => {
        if (primeiraConsulta.current) { primeiraConsulta.current = false; return; }
        let ativo = true;
        setErroFiscal(false);
        setErro(null);
        setConsultando(false);
        if (!props.nota.unidadeOrganizacional || !props.nota.transacaoNotaFiscal || !transacao || !props.nota.destinatario
            || !props.nota.enderecoDestinatario || !atual.current.produto) return;
        setConsultando(true);
        const timer = window.setTimeout(() => {
            const entrada = itens.entradaConsulta(atual.current, informados.current);
            service.consultarParametrosFiscais({ ...props.nota, itens: [entrada] })
                .then(([fiscal]) => {
                    if (!ativo) return;
                    setItem(anterior => itens.aplicarParametros(anterior, fiscal, informados.current));
                })
                .catch((error: unknown) => {
                    if (!ativo) return;
                    setErroFiscal(true);
                    const dados = isAxiosError(error) ? error.response?.data : null;
                    setErro(typeof dados === "string" ? dados : dados?.detail || dados?.message
                        || "Não foi possível consultar os parâmetros fiscais do item.");
                })
                .finally(() => { if (ativo) setConsultando(false); });
        }, 250);
        return () => { ativo = false; window.clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contexto]);

    useEffect(() => {
        let ativo = true;
        setLotes([]);
        setConsultandoLotes(false);
        if (!transacao?.movimentaEstoque || !item.produto?.codigo || !item.deposito?.codigo) return;
        setConsultandoLotes(true);
        service.getLotesDisponiveis(item.deposito.codigo, item.produto.codigo)
            .then(dados => { if (ativo) setLotes(dados); })
            .catch(() => { if (ativo) setErro("Não foi possível consultar os lotes do produto e depósito."); })
            .finally(() => { if (ativo) setConsultandoLotes(false); });
        return () => { ativo = false; };
    }, [item.produto?.codigo, item.deposito?.codigo, transacao?.movimentaEstoque]);

    function alterar(campos: Partial<ItemEmissaoNotaFiscalSaida>) {
        if ("cstIcms" in campos && campos.cstIcms !== "51") campos = { ...campos,
            percentualDiferimentoIcms: null, informarCalculoIcms51: null };
        if (campos.informarCalculoIcms51 === false && ((item.percentualDiferimentoIcms != null && item.percentualDiferimentoIcms !== 100)
            || (item.percentualReducaoBaseIcms ?? 0) > 0)) {
            setErro("Diferimento parcial ou redução da base exigem informar o cálculo do ICMS 51."); return;
        }
        if ("cstIbsCbs" in campos) campos = { ...campos,
            modalidadeTributacaoIbsCbs: ["400", "410"].includes(campos.cstIbsCbs ?? "") ? "NAO_TRIBUTADO" : "PERCENTUAL" };
        const proximo = { ...informados.current };
        for (const [campo, valor] of Object.entries(campos)) {
            if (valor == null || (typeof valor === "string" && !valor.trim())) delete proximo[campo as Campo];
            else Object.assign(proximo, { [campo]: valor });
        }
        informados.current = proximo;
        setItem(anterior => ({ ...anterior, ...campos }));
    }

    function escolherProduto(produto: Produto | null) {
        informados.current = {};
        setItem(anterior => ({ ...anterior,
            ...Object.fromEntries(camposTributariosItem.map(campo => [campo, null])),
            produto, deposito: null, lote: null, informarCalculoIcms51: null, codigoProdutoFiscal: produto?.codigo ?? "",
            descricaoProduto: produto?.nome ?? "", unidadeComercial: produto?.unidadeMedida ?? "UN",
            unidadeTributavel: produto?.unidadeTributavel ?? null, quantidadeTributavel: null,
            ncm: produto?.classificacaoFiscal?.ncm ?? "", cest: produto?.classificacaoFiscal?.cest ?? null,
            ean: produto?.ean ?? null, origemMercadoria: produto?.origemMercadoria ?? null, cfop: "" }));
    }

    function tributa(tributo: Tributo): boolean {
        const informado = item[`tributa${tributo}`];
        if (informado != null) return informado;
        if (tributo === "Ibs" || tributo === "Cbs") return item.modalidadeTributacaoIbsCbs
            ? item.modalidadeTributacaoIbsCbs !== "NAO_TRIBUTADO" : !["400", "410"].includes(item.cstIbsCbs ?? "");
        return transacao?.[`tributa${tributo}`] ?? false;
    }

    function indicador(tributo: Tributo, nome: string) {
        return <div className="col-12 mb-3">
            <Label>Tributa {nome}</Label>
            <Dropdown aria-label={`Tributa ${nome}`} value={tributa(tributo)} options={simNao} showClear
                placeholder="Padrão do cadastro" onChange={e => alterar({ [`tributa${tributo}`]: e.value ?? null })} className="w-100" />
        </div>;
    }

    function texto(campo: Campo, titulo: string, limite: number) {
        return <div className="col-12 col-md-6 mb-3" key={campo}>
            <Label>{titulo}</Label>
            <InputText aria-label={titulo} value={String(item[campo] ?? "")} maxLength={limite}
                onChange={e => alterar({ [campo]: e.target.value || null })} className="w-100" />
        </div>;
    }

    function numero(campo: Campo, titulo: string, percentual = false, desabilitado = false) {
        const valor = item[campo];
        const valorUnitario = campo === "valorUnitario";
        return <div className="col-12 col-md-6 mb-3" key={campo}>
            <Label>{titulo}</Label>
            <InputNumber aria-label={`${titulo} do item ${props.indice + 1}`} value={desabilitado ? 0 : typeof valor === "number" ? valor : null}
                locale="pt-BR" min={0} max={percentual ? 100 : campo === "quantidadeComercial" ? item.itemPedido?.qtDisponivelFaturamento : undefined}
                mode={valorUnitario ? "currency" : "decimal"} currency={valorUnitario ? "BRL" : undefined}
                minFractionDigits={valorUnitario ? 2 : undefined}
                maxFractionDigits={valorUnitario ? 2 : 4} 
                disabled={desabilitado || consultando}
                onFocus={valorUnitario ? e => {
                    const input = e.target;
                    window.requestAnimationFrame(() => input.select());
                } : undefined}
                onClick={valorUnitario ? e => {
                    if (e.target instanceof HTMLInputElement) e.target.select();
                } : undefined}
                onValueChange={e => alterar({ [campo]: e.value ?? null })} className="w-100" />
        </div>;
    }

    function salvar() {
        if (item.transacaoNotaFiscal && !itens.transacaoCompativel(item.transacaoNotaFiscal, props.nota)) {
            setErro("Selecione uma transação do item compatível com a finalidade e o destino da nota."); return;
        }
        if (!item.produto?.codigo || !item.ncm?.trim() || !item.cfop || !item.unidadeComercial?.trim()
            || !(item.quantidadeComercial > 0) || !(item.valorUnitario > 0)) {
            setErro("Informe produto, NCM, CFOP, unidade, quantidade e valor unitário válidos."); return;
        }
        if (!props.cfops.some(cfop => cfop.codigo === item.cfop)) {
            setErro("Selecione um CFOP compatível com a operação."); return;
        }
        if (item.itemPedido?.qtDisponivelFaturamento != null && item.quantidadeComercial > item.itemPedido.qtDisponivelFaturamento) {
            setErro("A quantidade excede o saldo disponível do pedido."); return;
        }
        if (transacao?.movimentaEstoque && (!item.deposito?.codigo || !item.lote)) {
            setErro("Informe depósito e lote para a movimentação de estoque."); return;
        }
        if (item.quantidadeTributavel != null && item.quantidadeTributavel <= 0) {
            setErro("A quantidade tributável deve ser positiva."); return;
        }
        if (item.cstIcms === "51" && !icms51SemCalculo && (item.percentualDiferimentoIcms == null
            || !Number.isFinite(item.percentualDiferimentoIcms)
            || item.percentualDiferimentoIcms < 0 || item.percentualDiferimentoIcms > 100)) {
            setErro("Informe o percentual de diferimento do ICMS entre 0 e 100%."); return;
        }
        for (const [tributo, nome, cst, tributados] of [
            ["Icms", "ICMS", item.cstIcms, ["00", "10", "20", "51", "70", "90"]],
            ["Ipi", "IPI", item.cstIpi, ["50", "99"]],
            ["Pis", "PIS", item.cstPis, ["01", "02", "03"]],
            ["Cofins", "COFINS", item.cstCofins, ["01", "02", "03"]]
        ] as const) {
            if (cst && tributa(tributo) !== (tributados as readonly string[]).includes(cst)) {
                setErro(`Confira o CST e o campo Tributa ${nome}: as opções devem ser compatíveis.`); return;
            }
        }
        if (item.cstIbsCbs && (tributa("Ibs") !== !["400", "410"].includes(item.cstIbsCbs)
            || tributa("Cbs") !== !["400", "410"].includes(item.cstIbsCbs))) {
            setErro("Confira Tributa IBS e Tributa CBS: ambos devem ser compatíveis com o CST e a classificação compartilhados."); return;
        }
        props.onSave({ ...item, camposInformados: Object.keys(informados.current),
            tributaIcms: tributa("Icms"), tributaIpi: tributa("Ipi"),
            tributaPis: tributa("Pis"), tributaCofins: tributa("Cofins"), tributaIbs: tributa("Ibs"), tributaCbs: tributa("Cbs") });
    }

    return <Dialog header={props.novo ? "Adicionar item" : `Editar item ${props.indice + 1}`} visible modal
        onHide={props.onHide} style={{ width: "min(1100px, 95vw)" }} breakpoints={{ "768px": "98vw" }}
        footer={<><Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={props.onHide} />
            <Button label="Salvar item" icon="pi pi-check" disabled={consultando || erroFiscal} onClick={salvar} /></>}>
        {erro && <div role="alert" className="alert alert-danger">{erro}</div>}
        {consultando && <p role="status">Selecionando parâmetros fiscais...</p>}
        <p>Os valores informados no item têm prioridade sobre os cadastros. Confira os CSTs ao alterar a tributação.</p>
        <TabView scrollable className="mb-3">
            <TabPanel header="Dados gerais"><div className="row">
                <div className="col-12 mb-3"><Label>Transação do item</Label>
                    <Dropdown aria-label="Transação do item" value={item.transacaoNotaFiscal?.id ?? null}
                        options={props.opcoes.transacoes.filter(t => itens.transacaoCompativel(t, props.nota))}
                        optionLabel="descricao" optionValue="id" filter showClear placeholder="Usar transação da nota"
                        onChange={e => setItem(anterior => itens.limparPadroes({ ...anterior,
                            transacaoNotaFiscal: props.opcoes.transacoes.find(t => t.id === e.value) ?? null }, informados.current))}
                        className="w-100" />
                    <small>{item.transacaoNotaFiscal ? "Transação própria do item." : `Usa a transação da nota: ${props.nota.transacaoNotaFiscal?.descricao ?? "não selecionada"}.`}</small>
                </div>
                <div className="col-12 mb-3"><Label obrigatorio>Produto</Label>
                    <Dropdown aria-label="Produto" disabled={!!item.itemPedido} value={item.produto?.codigo ?? null}
                        options={props.opcoes.produtos} optionLabel="nome" optionValue="codigo" filter showClear
                        onChange={e => escolherProduto(props.opcoes.produtos.find(p => p.codigo === e.value) ?? null)} className="w-100" /></div>
                {texto("descricaoProduto", "Descrição na nota", 120)}
                {numero("quantidadeComercial", "Quantidade")}{numero("valorUnitario", "Valor unitário")}
                {texto("unidadeComercial", "Unidade comercial", 6)}{texto("unidadeTributavel", "Unidade tributável", 6)}
                {numero("quantidadeTributavel", "Quantidade tributável")}
                {texto("ncm", "NCM", 8)}{texto("cest", "CEST", 7)}{texto("ean", "EAN", 14)}
                <div className="col-12 col-md-6 mb-3"><Label>Origem da mercadoria</Label>
                    <Dropdown aria-label="Origem da mercadoria" value={item.origemMercadoria} options={produtos.getOrigensMercadoria()} showClear
                        onChange={e => alterar({ origemMercadoria: e.value ?? null })} className="w-100" /></div>
                <div className="col-12 col-md-6 mb-3"><Label obrigatorio>CFOP</Label>
                    <Dropdown aria-label={`CFOP do item ${props.indice + 1}`} value={item.cfop || null}
                        options={props.cfops.map(cfop => ({ ...cfop, label: `${cfop.codigo} - ${cfop.descricao}` }))}
                        optionLabel="label" optionValue="codigo" filter showClear onChange={e => alterar({ cfop: e.value ?? "" })} className="w-100" /></div>
                {transacao?.movimentaEstoque && <>
                    <div className="col-12 col-md-6 mb-3"><Label obrigatorio>Depósito</Label>
                        <Dropdown aria-label="Depósito" value={item.deposito?.codigo ?? null} options={props.opcoes.depositos}
                            optionLabel="nome" optionValue="codigo" filter onChange={e => alterar({
                                deposito: props.opcoes.depositos.find(d => d.codigo === e.value) ?? null, lote: null })} className="w-100" /></div>
                    <div className="col-12 col-md-6 mb-3"><Label obrigatorio>Lote</Label>
                        <Dropdown aria-label="Lote" value={item.lote} options={lotes.map(lote => ({ ...lote, descricao: lote.lote || "Sem lote" }))}
                            optionLabel="descricao" optionValue="lote" filter showClear disabled={consultandoLotes}
                            onChange={e => alterar({ lote: e.value ?? null })} className="w-100" /></div>
                </>}
                {numero("valorDesconto", "Desconto")}{numero("valorFrete", "Frete do item")}
                {numero("valorSeguro", "Seguro do item")}{numero("valorOutrasDespesas", "Outras despesas do item")}
            </div></TabPanel>
            <TabPanel header="ICMS"><div className="row">
                {indicador("Icms", "ICMS")}{texto("cstIcms", "CST ICMS (Regime Normal)", 2)}
                {texto("csosn", "CSOSN (Simples Nacional)", 3)}
                {item.cstIcms === "51" && <div className="col-12 mb-3">
                    <Label>Informar cálculo do ICMS 51</Label>
                    <Dropdown aria-label="Informar cálculo do ICMS 51" value={item.informarCalculoIcms51 !== false}
                        options={simNao} onChange={e => alterar({ informarCalculoIcms51: e.value })} className="w-100" />
                    {icms51SemCalculo && <p>O XML informará somente origem, CST e modalidade da base. Este item não acrescentará base nem valor aos totais de ICMS.</p>}
                </div>}
                {!icms51SemCalculo && <>
                    {numero("aliquotaIcms", "Alíquota ICMS (%)", true, !tributa("Icms"))}
                    {numero("percentualReducaoBaseIcms", "Redução da base ICMS (%)", true)}
                </>}
                {item.cstIcms === "51" && !icms51SemCalculo && <>
                    {numero("percentualDiferimentoIcms", "Diferimento do ICMS (%)", true)}
                    <div className="col-12 mb-3">
                        <p>Para CST 51, mantenha Tributa ICMS em Sim. Informe 100% para diferimento total.</p>
                        <div>Prévia do ICMS da operação: {diferimentoIcms.operacao.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</div>
                        <div>Prévia do ICMS diferido: {diferimentoIcms.diferido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</div>
                        <div>Prévia do ICMS devido: {diferimentoIcms.devido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</div>
                    </div>
                </>}
                {numero("aliquotaIcmsUfDestino", "Alíquota ICMS UF destino (%)", true)}
                {numero("aliquotaFcpUfDestino", "FCP UF destino (%)", true)}
                {numero("aliquotaInterestadual", "Alíquota interestadual (%)", true)}
                {texto("beneficioFiscal", "Benefício fiscal", 10)}
            </div></TabPanel>
            <TabPanel header="IPI"><div className="row">
                {indicador("Ipi", "IPI")}{texto("cstIpi", "CST IPI", 2)}{texto("codigoEnquadramentoIpi", "Enquadramento do IPI", 3)}
                {numero("aliquotaIpi", "Alíquota IPI (%)", true, !tributa("Ipi"))}
            </div></TabPanel>
            <TabPanel header="PIS"><div className="row">
                {indicador("Pis", "PIS")}{texto("cstPis", "CST PIS", 2)}
                {numero("aliquotaPis", "Alíquota PIS (%)", true, !tributa("Pis"))}
            </div></TabPanel>
            <TabPanel header="COFINS"><div className="row">
                {indicador("Cofins", "COFINS")}{texto("cstCofins", "CST COFINS", 2)}
                {numero("aliquotaCofins", "Alíquota COFINS (%)", true, !tributa("Cofins"))}
            </div></TabPanel>
            <TabPanel header="IBS"><div className="row">
                {indicador("Ibs", "IBS")}{texto("cstIbsCbs", "CST IBS/CBS", 3)}
                {texto("classificacaoTributaria", "Classificação tributária IBS/CBS", 6)}
                {numero("aliquotaIbsEstadual", "Alíquota IBS estadual (%)", true, !tributa("Ibs"))}
                {numero("aliquotaIbsMunicipal", "Alíquota IBS municipal (%)", true, !tributa("Ibs"))}
            </div></TabPanel>
            <TabPanel header="CBS"><div className="row">
                {indicador("Cbs", "CBS")}{numero("aliquotaCbs", "Alíquota CBS (%)", true, !tributa("Cbs"))}
                <p>IBS e CBS compartilham o CST e a classificação tributária informados na aba IBS.</p>
            </div></TabPanel>
        </TabView>
        <div>Valor do IPI: {calculo.calcularIpi(item, transacao).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</div>
        <strong>Total do item: {calculo.calcularTotalItem(item, transacao).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</strong>
    </Dialog>;
}
